
import React, { useEffect, useState } from "react";
import RightTimeApi from "@/API";
import { FaPlusSquare } from "react-icons/fa";

import FeeScheduleItemModal from "@modals/FeeScheduleItemModal";
import FeeScheduleModal from "@modals/FeeScheduleModal";


export default function FeeScheduleTab({ isActive, updateSpinner }) {
  const [feeSchedules, setFeeSchedules] = useState([]); // Array of FeeScheduleViewModel
  const [selectedSchedule, setSelectedSchedule] = useState(null); // Selected FeeScheduleViewModel
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [lookups, setLookups] = useState(null);
  const [editSchedule, setEditSchedule] = useState(null);

  useEffect(() => {
    if (isActive) {
      getData();
    }
  }, [isActive]);

  async function getLookups() {
    if( lookups ) return; // Only fetch if not already loaded
    let resp = await RightTimeApi.getAsync("/AccountingLookups");
    if (resp && resp.ok && resp.jsonData) {
      setLookups(resp.jsonData);
    }
  }

  async function getData() {
    setLoading(true);
    setError(null);
    updateSpinner && updateSpinner(true);
    try {
      getLookups();
      const resp = await RightTimeApi.getAsync("/FeeSchedules");
      if (resp && resp.ok && resp.jsonData) {
        setFeeSchedules(resp.jsonData);
        // Only auto-select the first schedule if none is selected or the selected is not in the new list
        if (resp.jsonData.length > 0) {
          if (!selectedSchedule || !resp.jsonData.some(s => s.id === selectedSchedule.id)) {
            setSelectedSchedule(resp.jsonData[0]);
          }
          else {
            setSelectedSchedule(resp.jsonData.find(s => s.id === selectedSchedule.id) || resp.jsonData[0]);
          }

        } else {
          setSelectedSchedule(null);
        }
      } else {
        setError("Failed to load fee schedules");
      }
    } catch (error) {
      setError("Error loading fee schedules: " + error.message);
    } finally {
      setLoading(false);
      updateSpinner && updateSpinner(false);
    }
  }


  function handleScheduleClick(schedule) {
    if (!selectedSchedule) {
      setSelectedSchedule(schedule);
    } else if (selectedSchedule.id === schedule.id) {
      // Already selected, open modal for editing
      handleEditSchedule(schedule);
    } else {
      setSelectedSchedule(schedule);
    }
  }


  // FeeScheduleModal handlers
  function handleAddSchedule() {
    setEditSchedule({id:0, glAccountId: 52030});
  }
  function handleEditSchedule(schedule) {
    setEditSchedule(schedule);
  }

  // FeeScheduleItemModal handlers
  function handleAddItem() {
    var localGLAccountId = lookups.glAccounts.find(c => c.value === 'Professional Fees')?.key || 0;
    var localInterval = lookups.paymentIntervals.find(i => i.value === 'OneTime')?.key || "";
    setEditItem({ id: 0, feeScheduleId: selectedSchedule ? selectedSchedule.id : null, interval: localInterval, glAccountId: localGLAccountId });
  }
  function handleEditItem(item) {
    setEditItem(item);
  }

  // Called after FeeScheduleItemModal saves
  function handleItemModalSaved() {
    setEditItem(null);
    getData();
  }

  // The rest of the modal logic for items can be added here as needed

  return (
    <>
      <div className="horizontal">
        <div className="tableWrapper dimensionTable scrollable">
          <table>
            <caption>
              <span className="title">Fee Schedules
              {feeSchedules && (
                <>
                  <span className="captionCount">({feeSchedules.length})</span>
                  <span className="captionRight">
                    <FaPlusSquare className="actionIcon" onClick={handleAddSchedule} title="Add Fee Schedule" />
                  </span>
                </>
              )}</span>
            </caption>
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Effective Date</th>
                <th>Expiration Date</th>
              </tr>
            </thead>
            <tbody>
              {feeSchedules.length > 0 ? (
                feeSchedules.map((schedule) => (
                  <tr
                    key={schedule.id}
                    className={`${selectedSchedule && selectedSchedule.id === schedule.id ? 'active' : ''} clickableRow`}
                    onClick={() => handleScheduleClick(schedule)}
                  >
                    <td>{schedule.name}</td>
                    <td>{schedule.description}</td>
                    <td>{schedule.effectiveDate ? new Date(schedule.effectiveDate).toLocaleDateString() : ''}</td>
                    <td>{schedule.expirationDate ? new Date(schedule.expirationDate).toLocaleDateString() : ''}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="center">No fee schedules found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="tableWrapper factTable horizontal">
          <table>
            <caption>
              <span className="title">Fee Schedule Items</span>
              {selectedSchedule && (
                <span className="captionRight">
                  <FaPlusSquare className="actionIcon clickableIcon" onClick={handleAddItem} title="Add Fee Schedule Item" />
                </span>
              )}
            </caption>
            <thead>
              <tr>
                <th>Item Name</th>
                <th>Amount</th>
                <th>Notes</th>
                <th>GL Account</th>
                <th>Interval</th>
              </tr>
            </thead>
            <tbody>
              {lookups && selectedSchedule && selectedSchedule.items && selectedSchedule.items.length > 0 ? (
                selectedSchedule.items
                  .sort((a, b) => (a.itemName || '').localeCompare(b.itemName || ''))
                  .map((item) => (
                  <tr
                    key={item.id}
                    className="clickableRow"
                    onClick={() => handleEditItem(item)}
                  >
                    <td className="left">{item.itemName}</td>
                    <td className="right">{item.amount}</td>
                    <td>{item.notes}</td>
                    <td className="left">{item.glAccountName}</td>
                    <td>{lookups.paymentIntervals.find(l => l.key === item.interval)?.value || item.interval}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="center">No items for this schedule</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <FeeScheduleModal
        isOpen={!!editSchedule}
        schedule={editSchedule}
        onClose={() => { setEditSchedule(null); getData(); }}
        updateSpinner={updateSpinner}
      />
      <FeeScheduleItemModal
        isOpen={!!editItem}
        item={editItem}
        feeScheduleId={selectedSchedule ? selectedSchedule.id : null}
        onClose={() => { setEditItem(null); getData(); }}
        updateSpinner={updateSpinner}
        lookups={lookups}
      />
    </>
  );
}


